import { injectHtmlPreviewCsp } from './code_file';

// Serialized into the preview before generated scripts. Each document gets its
// own disposable stores; never bridge to the parent or relax the iframe sandbox.
/* eslint-disable no-var */
export function installHtmlPreviewStorage() {
  function memoryStorage() {
    var values = Object.create(null);
    var storage = Object.create(null);
    Object.defineProperties(storage, {
      length: { configurable: true, get: function () { return Object.keys(values).length; } },
      key: { configurable: true, value: function (index) { var keys = Object.keys(values); index = Number(index) >>> 0; return index < keys.length ? keys[index] : null; } },
      getItem: { configurable: true, value: function (key) { key = String(key); return Object.prototype.hasOwnProperty.call(values, key) ? values[key] : null; } },
      setItem: { configurable: true, value: function (key, value) { values[String(key)] = String(value); } },
      removeItem: { configurable: true, value: function (key) { delete values[String(key)]; } },
      clear: { configurable: true, value: function () { values = Object.create(null); } },
    });
    return new Proxy(storage, {
      get: function (target, key) { return Object.prototype.hasOwnProperty.call(target, key) ? target[key] : values[key]; },
      set: function (target, key, value) { if (typeof key === 'string') values[key] = String(value); return true; },
      deleteProperty: function (target, key) { delete values[key]; return true; },
      has: function (target, key) { return key in target || key in values; },
      ownKeys: function () { return Object.keys(values); },
      getOwnPropertyDescriptor: function (target, key) {
        if (Object.prototype.hasOwnProperty.call(values, key)) return { configurable: true, enumerable: true, writable: true, value: values[key] };
        return Object.getOwnPropertyDescriptor(target, key);
      },
    });
  }
  ['localStorage', 'sessionStorage'].forEach(function (name) {
    try {
      // Reading even the property throws for an opaque-origin srcdoc iframe.
      // Leave available storage alone (native previews already isolate it).
      window[name].getItem('__gab_preview_storage_probe__');
      // Some privacy modes allow reads but reject every write.
      var probe = '__gab_preview_probe_' + Math.random().toString(36).slice(2);
      window[name].setItem(probe, probe);
      window[name].removeItem(probe);
    } catch (_error) {
      try { Object.defineProperty(window, name, { configurable: true, value: memoryStorage() }); } catch (_ignored) { /* Browser does not permit a fallback. */ }
    }
  });
}

// Untrusted messages are only diagnostics; they never authorize a host action.
export function installHtmlPreviewDiagnostics(session, parentOrigin) {
  var seen = Object.create(null), count = 0;
  function send(type, message) {
    window.parent.postMessage({ channel: 'gab-html-runtime-v1', session: session, type: type, message: message }, parentOrigin);
  }
  function report(message) {
    message = String(message || 'The page encountered an error.').slice(0, 500);
    if (seen[message] || count >= 10) return;
    seen[message] = true; count += 1; send('error', message);
  }
  window.addEventListener('error', function (event) {
    if (event.target && event.target !== window) {
      if (/^(SCRIPT|LINK|IMG|VIDEO|AUDIO)$/.test(event.target.tagName)) report('A page resource could not load. Check its URL and network access.');
    } else report(event.message);
  }, true);
  window.addEventListener('unhandledrejection', function (event) { report(event.reason && event.reason.message || event.reason); });
  document.addEventListener('securitypolicyviolation', function (event) { report('The page blocked a resource (' + event.effectiveDirective + ').'); });
  window.addEventListener('load', function () { send('loaded', ''); }, { once: true });
  // A ready message does not assert that the user's game initialized correctly.
  send('installed', '');
}
/* eslint-enable no-var */

export function withHtmlPreviewDiagnostics(preparedHtml, session, parentOrigin) {
  const end = preparedHtml.indexOf('>', preparedHtml.indexOf('<meta http-equiv="Content-Security-Policy"')) + 1;
  const args = JSON.stringify([session, parentOrigin]).slice(1, -1).replace(/</g, '\\u003c');
  return preparedHtml.slice(0, end) + `<script>(${installHtmlPreviewDiagnostics.toString()})(${args});</script>` + preparedHtml.slice(end);
}

export function prepareHtmlPreview(html) {
  const safeHtml = injectHtmlPreviewCsp(html);
  if (!safeHtml) return '';
  const cspEnd = safeHtml.indexOf('>', safeHtml.indexOf('<meta http-equiv="Content-Security-Policy"')) + 1;
  return safeHtml.slice(0, cspEnd) + `<script>(${installHtmlPreviewStorage.toString()})();</script>` + safeHtml.slice(cspEnd);
}
